import React, { FC, useCallback, useEffect, useState } from "react";

import { ArchivedSessionsDataGridProps } from "./Types";
import { TrainingType } from "./Types";
import { getSessionColumns } from "./utils/gridColumns";
import { useTime } from "@/app/application/TimeContext";
import { Translate } from "@/app/application/Translate";
import { SessionType } from "@/features/training/model/Training/TrainingDrill";
import { TrainingDrillSessionSummary } from "@/features/training/model/Training/TrainingDrillSession";
import { TrainingSessionStatus } from "@/features/training/model/Training/TrainingSession";
import { Box, Skeleton, Typography, alpha, useTheme } from "@mui/material";
import { DataGrid, GridRowParams } from "@mui/x-data-grid";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import {
	useFetchCourseSessions,
	useFetchDrillSessions,
} from "@/service/TrainingQueries";

dayjs.extend(utc);

const DASHBOARD_GRID_HEIGHT = 292;

export const ArchivedSessionsDataGridDashboard: FC<
	ArchivedSessionsDataGridProps
> = ({ startDate, endDate, trainingType }) => {
	const { timezone } = useTime();
	const theme = useTheme();

	const [sessions, setSessions] = useState<TrainingDrillSessionSummary[]>([]);

	const queryOptions = {
		StartDate: startDate ? dayjs(startDate).format("YYYY.MM.DD") : undefined,
		EndDate: endDate ? dayjs(endDate).format("YYYY.MM.DD") : undefined,
		Status: TrainingSessionStatus.Archived,
	};

	const isCourseSessionsActive =
		trainingType === TrainingType.All || trainingType === TrainingType.Courses;
	const isDrillSessionsActive =
		trainingType === TrainingType.All ||
		trainingType === TrainingType.Exercises ||
		trainingType === TrainingType.Events;

	const { isLoading, data: courseSessions } = useFetchCourseSessions(
		queryOptions,
		isCourseSessionsActive,
	);
	const { isLoading: isLoadingDrillSessions, data: drillSessions } =
		useFetchDrillSessions(queryOptions, isDrillSessionsActive);

	// Handle session merging
	useEffect(() => {
		if (!isCourseSessionsActive && !isDrillSessionsActive) return;

		const merged: TrainingDrillSessionSummary[] = [];

		if (isCourseSessionsActive && courseSessions) {
			merged.push(
				...courseSessions.map(
					(session) =>
						({
							Id: session.Id,
							Name: session.Name,
							Date: session.Date,
							DrillId: session.CourseId,
							Type: SessionType.Course,
						}) as TrainingDrillSessionSummary,
				),
			);
		}

		if (isDrillSessionsActive && drillSessions) {
			const filteredDrills =
				trainingType === TrainingType.Exercises
					? drillSessions.filter((x) => x.Type === SessionType.Exercise)
					: trainingType === TrainingType.Events
						? drillSessions.filter((x) => x.Type === SessionType.Event)
						: drillSessions;

			merged.push(...filteredDrills);
		}

		setSessions(merged);
	}, [
		trainingType,
		courseSessions,
		drillSessions,
		isCourseSessionsActive,
		isDrillSessionsActive,
	]);

	// Navigation
	const handleNavigate = useCallback((row: TrainingDrillSessionSummary) => {
		// const state = row.CourseId
		// ? { courseId: row.CourseId }
		// : { drillId: row.DrillId };
		window.open(`/training/archive/${row.Id}`, "_blank", "noopener,noreferrer");
	}, []);

	// Loading state
	if (isLoading || isLoadingDrillSessions) {
		return (
			<Box sx={{ mt: 1 }}>
				<Skeleton
					variant="rectangular"
					height={36}
					sx={{ mb: 1, borderRadius: 1 }}
				/>
				<Skeleton
					variant="rectangular"
					height={36}
					sx={{ mb: 1, borderRadius: 1 }}
				/>
				<Skeleton
					variant="rectangular"
					height={36}
					sx={{ mb: 1, borderRadius: 1 }}
				/>
				<Skeleton
					variant="rectangular"
					height={36}
					sx={{ mb: 1, borderRadius: 1 }}
				/>
				<Skeleton variant="rectangular" height={36} sx={{ borderRadius: 1 }} />
			</Box>
		);
	}

	// Empty state
	if (!sessions.length) {
		return (
			<Box
				display="flex"
				flexDirection="column"
				justifyContent="center"
				alignItems="center"
				height={200}
				sx={{
					textAlign: "center",
					px: 2,
					borderRadius: 2,
					border: `1px dashed ${alpha(theme.palette.divider, 0.85)}`,
					bgcolor:
						theme.palette.mode === "dark"
							? alpha(theme.palette.common.white, 0.02)
							: alpha(theme.palette.primary.main, 0.02),
				}}
			>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
					<Translate String="Training.Archive.NoSessionsFound">
						No sessions found in this period
					</Translate>
				</Typography>
				<Typography variant="caption" color="text.secondary">
					<Translate String="Training.Archive.NoSessionsHint">
						Try a different date range or training type.
					</Translate>
				</Typography>
			</Box>
		);
	}

	// Main Table
	return (
		<Box mt={1}>
			<Box sx={{ height: DASHBOARD_GRID_HEIGHT, minHeight: DASHBOARD_GRID_HEIGHT }}>
				<DataGrid<TrainingDrillSessionSummary>
					rows={sessions}
					columns={getSessionColumns(handleNavigate, timezone)}
					getRowId={(row: TrainingDrillSessionSummary) => `${row.Id}-${row.Type}`}
					disableColumnFilter
					disableDensitySelector
					disableColumnSelector
					pageSizeOptions={[5, 10, 25]}
					initialState={{
						pagination: { paginationModel: { pageSize: 5, page: 0 } },
					}}
					onRowClick={(params: GridRowParams<TrainingDrillSessionSummary>) =>
						handleNavigate(params.row)
					}
					sx={{
						height: "100%",
						border: "none",
						fontSize: theme.typography.body2.fontSize,
						"& .MuiDataGrid-columnHeaders": { display: "none" },
						"& .MuiDataGrid-cell": {
							borderBottom: "none",
							py: 0.5,
						},
						"& .MuiDataGrid-row": {
							borderRadius: 1.5,
							"&:nth-of-type(even)": {
								bgcolor: alpha(theme.palette.primary.main, 0.02),
							},
							"&:hover": {
								cursor: "pointer",
								backgroundColor:
									theme.palette.mode === "light"
										? alpha(theme.palette.primary.main, 0.06)
										: alpha(theme.palette.primary.main, 0.18),
							},
						},
						"& .MuiDataGrid-virtualScroller": {
							overflowX: "hidden",
						},
						"& .MuiDataGrid-footerContainer": {
							borderTop: "none",
							minHeight: 40,
							px: 0.5,
							justifyContent: "flex-end",
						},
					}}
				/>
			</Box>
		</Box>
	);
};

export default ArchivedSessionsDataGridDashboard;
